import React, { Dispatch, ReactElement, SetStateAction, useState } from "react";
import { useTranslation } from "utilities/i18n";
import { PolyIcon, ToolTip } from "elements";
import { humanReadable } from "utilities/numberUtils";

export interface ModalMetric {
  type: string;
  count: number | string;
}

export type FiltersSelectedFcn = (obj: Object) => boolean;
export type FilterActionFcn = () => void;

interface PanelProps<T> {
  filters?: Array<ReactElement<T>>;
  areFiltersSelected?: FiltersSelectedFcn;
  isGeofilterActive?: FiltersSelectedFcn;
  filterValues?: T;
  resetFilters?: FilterActionFcn;
  clearGeofilter?: FilterActionFcn;
  metric?: ModalMetric;
  iconName?: string;
  subHeader?: string;
  appendix?: ReactElement;
  collapsible?: boolean;
  geofiltered?: boolean;
}

export interface CollapseIconProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export const CollapseIcon: React.FC<CollapseIconProps> = ({
  open,
  setOpen
}) => {
  return (
    <div
      className={`collapse-icon ${open ? "open" : ""}`}
      onClick={_ => setOpen(!open)}
      data-testid="collapse-icon"
    >
      <div />
    </div>
  );
};

type FilterPanelType<T = any> = React.FC<PanelProps<T>>;
export const FilterPanel: FilterPanelType = ({
  filters = [],
  areFiltersSelected,
  isGeofilterActive,
  filterValues,
  resetFilters,
  clearGeofilter,
  metric,
  iconName,
  subHeader,
  appendix,
  collapsible = false,
  geofiltered = false
}) => {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  let filtersSelected = false;
  if (areFiltersSelected && filterValues) {
    filtersSelected = areFiltersSelected(filterValues);
  }

  let geofilterActive = false;
  if (isGeofilterActive && filterValues) {
    geofilterActive = isGeofilterActive(filterValues);
  }

  let clearFiltersVisibility = "";
  if (filtersSelected && filters.length) {
    clearFiltersVisibility = "visible";
  }

  let clearGeofilterVisibility = "";
  if (geofilterActive) {
    clearGeofilterVisibility = "visible";
  }

  const doResetFilters = () => {
    clearGeofilter && clearGeofilter();
    resetFilters && resetFilters();
  };

  const doClearGeofilter = () => {
    clearGeofilter && clearGeofilter();
  };

  const metricOutput = metric ? humanReadable(metric.count) : undefined;

  return (
    <>
      {collapsible && <CollapseIcon open={open} setOpen={setOpen} />}
      <div
        className={`filter-group ${open ? "" : "collapsed"}`}
        data-testid="filter-modal-test"
      >
        {metric && (
          <div className="filter-metric">
            <div className="metric" data-testid="filter-modal-metric">
              <ToolTip
                iconReplacement={<></>}
                iconPosition="right"
                iconTitle="info"
                size="xs"
                position="bottom"
                title={metricOutput!.hrOutput}
                disablePopup={!metricOutput!.isAbbreviated}
              >
                <div className="metric-tooltip">{metricOutput!.fmtOutput}</div>
              </ToolTip>
            </div>
            <p className="metric-type">
              {metric.type}
              {iconName && <PolyIcon icon={iconName} className="white" />}
            </p>
            {subHeader && <p className="metric-sub-header">{subHeader}</p>}
          </div>
        )}
        <div className="filters">
          <span
            onClick={doResetFilters}
            className={`filter-action-button ${clearFiltersVisibility}`}
            data-testid="clear-filters-button"
            role="button"
          >
            <PolyIcon icon="clear_x" />
            {t("CLEAR_FILTERS")}
          </span>
          {geofiltered ? (
            <span
              onClick={doClearGeofilter}
              className={`filter-action-button ${clearGeofilterVisibility}`}
              data-testid="clear-geofilter-button"
              role="button"
            >
              <PolyIcon icon="clear_x" />
              {t("CLEAR_GEOFILTER")}
            </span>
          ) : null}
          <ul className="filter-list">
            {filters.map((filter, index) => (
              <li className="filter-list-item" key={`${index}-filter`}>
                {filter}
              </li>
            ))}
          </ul>
        </div>
        {appendix}
      </div>
    </>
  );
};

export default FilterPanel;
